import React, { useEffect, useRef, useState } from 'react'
import { AuthUser, ApiError, api } from '../lib/api'

interface Props {
  user: AuthUser
  onSignOut: () => void
  onHelp: () => void
  onExport: () => void
  onImport: () => void
  onImportStatement: () => void
  onCompareSpending: () => void
  /** Only passed when this browser can install the app. */
  onInstall?: () => void
  /** Only passed inside the store apps, which schedule the notifications. */
  onReminders?: () => void
  onDeleteAccount: () => void
}

/**
 * The header's overflow menu: who you are, and the handful of actions that
 * don't earn a permanent button.
 *
 * On a phone there is no room for Help / Export / Import along the top, and on
 * a desktop they were already the least-used controls up there, so they live
 * here at both sizes rather than appearing and disappearing with the viewport.
 */
export default function AccountMenu({
  user,
  onSignOut,
  onHelp,
  onExport,
  onImport,
  onImportStatement,
  onCompareSpending,
  onInstall,
  onReminders,
  onDeleteAccount,
}: Props) {
  const [open, setOpen] = useState(false)
  const [resend, setResend] = useState<'idle' | 'sending' | 'sent' | string>('idle')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  async function sendVerification() {
    setResend('sending')
    try {
      await api.resendVerification()
      setResend('sent')
    } catch (e) {
      setResend(e instanceof ApiError ? e.message : "Couldn't send that. Try again shortly.")
    }
  }

  const item =
    'w-full text-left px-4 py-2.5 text-sm text-ink/80 hover:bg-paper hover:text-ink'

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account and options"
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm border border-line bg-white text-ink/80 hover:bg-paper"
      >
        <span
          className="w-6 h-6 rounded-full bg-accent-soft text-accent text-xs font-sans flex items-center justify-center uppercase"
          aria-hidden
        >
          {user.email.slice(0, 1)}
        </span>
        {/* A dot on the avatar carries the "unconfirmed" state when the menu is shut. */}
        {!user.verified && <span className="w-1.5 h-1.5 rounded-full bg-rag-medium" aria-hidden />}
        <span className="text-xs hidden lg:inline max-w-[14ch] truncate">{user.email}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-64 bg-white border border-line rounded shadow-lg z-50 py-1"
        >
          <div className="px-4 py-2 border-b border-line">
            <p className="text-xs text-ink/70">Signed in as</p>
            <p className="text-sm text-ink break-all">{user.email}</p>
          </div>

          {!user.verified && (
            <div className="px-4 py-3 border-b border-line bg-rag-mediumBg/60">
              <p className="text-xs text-ink/80">
                Your email address isn't confirmed yet. Confirming it is what makes a password
                reset possible.
              </p>
              {resend === 'sent' ? (
                <p className="text-xs text-accent mt-1.5">Sent — check your inbox.</p>
              ) : (
                <button
                  onClick={sendVerification}
                  disabled={resend === 'sending'}
                  className="text-xs text-accent hover:underline mt-1.5 disabled:opacity-50"
                >
                  {resend === 'sending' ? 'Sending…' : 'Send the link again'}
                </button>
              )}
              {resend !== 'idle' && resend !== 'sending' && resend !== 'sent' && (
                <p className="text-xs text-rag-bad mt-1.5">{resend}</p>
              )}
            </div>
          )}

          <button role="menuitem" className={item} onClick={() => { setOpen(false); onHelp() }}>
            How this works
          </button>
          <button role="menuitem" className={item} onClick={() => { setOpen(false); onImportStatement() }}>
            Import bank statement
          </button>
          <button role="menuitem" className={item} onClick={() => { setOpen(false); onCompareSpending() }}>
            Compare my spending
          </button>
          <button role="menuitem" className={item} onClick={() => { setOpen(false); onExport() }}>
            Export data
          </button>
          <button role="menuitem" className={item} onClick={() => { setOpen(false); onImport() }}>
            Import data
          </button>
          {onReminders && (
            <button role="menuitem" className={item} onClick={() => { setOpen(false); onReminders() }}>
              Reminders
            </button>
          )}
          {onInstall && (
            <button role="menuitem" className={item} onClick={() => { setOpen(false); onInstall() }}>
              Install app
            </button>
          )}
          {/* New tabs, so reading them never interrupts a save still in flight. */}
          <a
            role="menuitem"
            href="/privacy"
            target="_blank"
            rel="noopener"
            className={`block ${item}`}
            onClick={() => setOpen(false)}
          >
            Privacy policy
          </a>
          <a
            role="menuitem"
            href="/terms"
            target="_blank"
            rel="noopener"
            className={`block ${item}`}
            onClick={() => setOpen(false)}
          >
            Terms of use
          </a>

          <div className="border-t border-line mt-1 pt-1">
            <button
              role="menuitem"
              className={`${item} text-rag-bad hover:text-rag-bad`}
              onClick={() => { setOpen(false); onSignOut() }}
            >
              Sign out
            </button>
            <button
              role="menuitem"
              className={`${item} text-xs text-ink/70`}
              onClick={() => { setOpen(false); onDeleteAccount() }}
            >
              Delete account…
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
